<template>
  <DefaultCrudView
    @create-new="newCustomer"
    :breadcrumbsItems="breadcrumbs"
    :actions="crudActions"
    containerLarge
  >
    <template slot="content">

      <v-data-table
        :headers="headers"
        :items="customers"
        class="elevation-1 compact-table"
        :loading="loading"
        :options.sync="options"
        :server-items-length="itemsLength"
        :footer-props="$defaultDataTableFooterProps"
      >
        <template v-slot:[`item.tenant_id`]="{ item }">
          {{ getTenantTitle(item.tenant_id) }}
        </template>

        <template v-slot:[`item.active`]="{ item }">
          <v-chip small :color="item.active ? 'success' : 'error'" dark>
            {{ item.active ? $t('yes') : $t('no') }}
          </v-chip>
        </template>

        <template v-slot:[`item.created_at`]="{ item }">
          {{ formatDate(item.created_at) }}
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-menu offset-y left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>mdi-dots-horizontal</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="editCustomer(item)">
                <v-list-item-icon>
                  <v-icon medium>mdi-pencil</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ $t('edit') }}</v-list-item-title>
              </v-list-item>

              <v-list-item @click="manageDeliverables(item)">
                <v-list-item-icon>
                  <v-icon medium>mdi-package-variant</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ $t('manage_deliverables') }}</v-list-item-title>
              </v-list-item>

              <v-list-item @click="manageBillables(item)">
                <v-list-item-icon>
                  <v-icon medium>mdi-cash-multiple</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ $t('manage_billables') }}</v-list-item-title>
              </v-list-item>

              <v-list-item @click="manageStatuses(item)">
                <v-list-item-icon>
                  <v-icon medium>mdi-flag-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ $t('manage_statuses') }}</v-list-item-title>
              </v-list-item>

              <v-list-item @click="manageAcls(item)">
                <v-list-item-icon>
                  <v-icon medium>mdi-shield-key-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ $t('manage_acls') }}</v-list-item-title>
              </v-list-item>

              <v-list-item @click="manageCompanies(item)">
                <v-list-item-icon>
                  <v-icon medium>mdi-office-building-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ $t('manage_companies') }}</v-list-item-title>
              </v-list-item>

              <v-divider></v-divider>

              <v-list-item v-if="item.active" @click="toggleActive(item, false)">
                <v-list-item-icon>
                  <v-icon medium>mdi-account-off-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ $t('deactivate_customer') }}</v-list-item-title>
              </v-list-item>

              <v-list-item v-if="!item.active" @click="toggleActive(item, true)">
                <v-list-item-icon>
                  <v-icon medium>mdi-account-check-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ $t('activate_customer') }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>

      </v-data-table>

      <DefaultModal :title="getModalTitle()" ref="modal" @submit="save">
        <template v-slot:content>
          <DynamicFormContent :fields="fields"></DynamicFormContent>
        </template>
      </DefaultModal>

      <CompanyCustomerModal ref="companyCustomerModal" />

    </template>
  </DefaultCrudView>
</template>

<style scoped>
  .container {
    padding: 0;
  }

  .table-container {
    margin-top: 20px;
  }

  ::v-deep .compact-table .v-data-table__wrapper td,
  ::v-deep .compact-table .v-data-table__wrapper th {
    padding: 6px 8px !important;
  }
</style>

<script>
import { mapActions, mapGetters } from 'vuex'

import DefaultCrudView from '@/components/DefaultCrudView.vue'
import DefaultModal from '@/components/DefaultModal.vue'
import DynamicFormContent from '@/components/DynamicFormContent.vue'
import CompanyCustomerModal from '@/components/business/CompanyCustomerModal.vue'

import AppActions from '@/store/app/actions-types'
import TenantActions from '@/store/core/tenant/actions-types'

import CustomerAPI from '@/services/api/business/customer'

export default {
  name: 'BusinessCustomers',

  components: {
    DefaultCrudView,
    DefaultModal,
    DynamicFormContent,
    CompanyCustomerModal,
  },

  computed: {
    ...mapGetters('app', [
      'getUserApplicationById'
    ]),

    breadcrumbs() {
      return [
        {
          text: this.$t('settings'),
          prependIcon: 'mdi-cog-outline',
          iconColor: 'primary',
          to: {
            name: 'DeliverableSettings',
          },
          disabled: false,
          exact: true,
          ripple: true,
        },
        {
          text: this.$t('customers'),
          prependIcon: 'mdi-account-group',
        },
      ]
    },

    fields() {
      if (this.selectedCustomer) {
        // Edit mode: customer_name + active
        return [
          {
            key: 'customer_name',
            name: 'customer_name',
            label: this.$t('customer_name'),
            type: 'text',
            value: this.selectedCustomer.customer_name,
            required: true,
            rules: [v => !!v || this.$t('field_required')],
            cols: 12,
          },
          {
            key: 'active',
            name: 'active',
            label: this.$t('active'),
            type: 'switch',
            value: this.selectedCustomer.active,
            cols: 12,
          },
        ]
      }

      // Create mode: tenant_id + customer_name
      return [
        {
          key: 'tenant_id',
          name: 'tenant_id',
          label: this.$t('tenant'),
          type: 'select',
          value: '',
          required: true,
          rules: [v => !!v || this.$t('field_required')],
          items: this.tenants.map(tenant => ({
            text: tenant.title || tenant.tenant_title,
            value: tenant.tenant_id,
          })),
          itemText: 'text',
          itemValue: 'value',
          cols: 12,
        },
        {
          key: 'customer_name',
          name: 'customer_name',
          label: this.$t('customer_name'),
          type: 'text',
          value: '',
          required: true,
          rules: [v => !!v || this.$t('field_required')],
          cols: 12,
        },
      ]
    },
  },

  data() {
    return {
      loading: true,
      customers: [],
      options: {
        sortBy: ['customer_name'],
        sortDesc: [false],
      },
      itemsLength: 0,
      crudActions: [
        {
          text: 'create_customer',
          eventToEmit: 'create-new',
        },
      ],
      tenants: [],
      selectedCustomer: null,
      headers: [
        { text: this.$t('customer_name'), value: 'customer_name', sortable: true },
        { text: this.$t('tenant'), value: 'tenant_id', sortable: true },
        { text: this.$t('active'), value: 'active', sortable: true },
        { text: this.$t('created_at'), value: 'created_at', sortable: true },
        { text: this.$t('actions'), value: 'actions', sortable: false },
      ],
    }
  },

  mounted() {
    this.selectedCustomer = null

    this[TenantActions.GET_ALL_TENANTS]()
      .then((tenants) => {
        this.tenants = tenants.items
      })
  },

  watch: {
    options: {
      handler() {
        this.reloadData()
      },
      deep: true,
    },
  },

  methods: {
    ...mapActions('core/tenant', [
      TenantActions.GET_ALL_TENANTS,
    ]),

    ...mapActions('app', [
      AppActions.OPEN_APP_SUCCESS_MESSAGE,
    ]),

    newCustomer() {
      this.selectedCustomer = null
      this.$refs.modal.openModal()
    },

    editCustomer(customer) {
      this.selectedCustomer = customer
      this.$refs.modal.openModal()
    },

    save() {
      if (!this.$refs.modal) {
        console.error('Modal reference not found')
        return
      }

      this.$refs.modal.errorMessage = null
      this.$refs.modal.errorList = []

      const formData = {}
      this.fields.forEach(field => {
        formData[field.name] = field.value
      })

      let apiCall

      if (this.selectedCustomer) {
        const customerId = this.selectedCustomer.customer_id

        if (!customerId) {
          console.error('Customer ID not found in selected customer object:', this.selectedCustomer)
          this.$refs.modal.errorMessage = this.$t('customer_id_not_found')
          return
        }

        apiCall = CustomerAPI.updateCustomer(customerId, {
          customer_name: formData.customer_name,
          active: formData.active,
        })
      } else {
        if (!formData.tenant_id) {
          this.$refs.modal.errorMessage = this.$t('tenant_required')
          this.$refs.modal.submitting = false
          return
        }

        apiCall = CustomerAPI.createCustomer({
          tenant_id: formData.tenant_id,
          customer_name: formData.customer_name,
        })
      }

      apiCall
        .then(() => {
          const message = this.selectedCustomer
            ? this.$t('customer_updated_successfully')
            : this.$t('customer_created_successfully')
          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](message)
          this.$refs.modal.closeModal()
          this.reloadData()
        })
        .catch((error) => {
          this.$refs.modal.submitting = false
          if (error.response && error.response.data) {
            this.$refs.modal.errorMessage = error.response.data.message || this.$t('error')
          }
        })
    },

    toggleActive(customer, activeValue) {
      const customerId = customer.customer_id

      if (!customerId) {
        console.error('Customer ID not found:', customer)
        return
      }

      CustomerAPI.updateCustomer(customerId, {
        customer_name: customer.customer_name,
        active: activeValue,
      })
        .then(() => {
          const message = activeValue
            ? this.$t('customer_activated_successfully')
            : this.$t('customer_deactivated_successfully')
          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](message)
          this.reloadData()
        })
        .catch((error) => {
          console.error('Error updating customer active status:', error)
        })
    },

    reloadData() {
      let options = { ...this.$route.query }

      if (this.options.sortBy.length > 0) {
        options.order = this.options.sortBy[0]
      }

      if (this.options.sortDesc.length > 0 && this.options.sortDesc[0]) {
        options.direction = 'DESC'
      }

      options.limit = this.options.itemsPerPage
      options.offset = this.options.itemsPerPage * (this.options.page - 1)

      this.loading = true
      CustomerAPI.getAllCustomers(options)
        .then((result) => {
          this.customers = result.data.items || []
          this.itemsLength = result.data.total || 0
          this.loading = false
        })
        .catch(() => {
          this.loading = false
        })
    },

    getTenantTitle(id) {
      const tenant = this.tenants.find(t => t.tenant_id == id)
      return tenant ? (tenant.title || tenant.tenant_title) : ''
    },

    getModalTitle() {
      return this.selectedCustomer ? this.$t('edit_customer') : this.$t('new_customer')
    },

    manageDeliverables(customer) {
      this.$router.push({
        name: 'CustomerDeliverables',
        params: {
          customerId: customer.customer_id,
        },
        query: {
          customerName: customer.customer_name,
        },
      })
    },

    manageBillables(customer) {
      this.$router.push({
        name: 'CustomerBillables',
        params: {
          customerId: customer.customer_id,
        },
        query: {
          customerName: customer.customer_name,
        },
      })
    },

    manageStatuses(customer) {
      this.$router.push({
        name: 'CustomerStatuses',
        params: {
          customerId: customer.customer_id,
        },
        query: {
          customerName: customer.customer_name,
        },
      })
    },

    manageAcls(customer) {
      this.$router.push({
        name: 'CustomerAcls',
        params: {
          customerId: customer.customer_id,
        },
        query: {
          customerName: customer.customer_name,
        },
      })
    },

    manageCompanies(customer) {
      this.$refs.companyCustomerModal.openModal(customer)
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return date.toLocaleDateString()
    },
  },
}
</script>
